import type { MatchSpan } from './types';

/**
 * Merge overlapping/adjacent covered ranges into a single coverage map so
 * matchCoverage doesn't double-count overlapping extractor hits.
 */
export function coveredLength(spans: ReadonlyArray<MatchSpan>, queryLength: number): number {
  if (spans.length === 0 || queryLength === 0) return 0;

  const ranges = spans
    .map(s => ({
      start: Math.max(0, Math.min(s.start, queryLength)),
      end: Math.max(0, Math.min(s.end, queryLength)),
    }))
    .filter(r => r.end > r.start)
    .sort((a, b) => a.start - b.start);

  if (ranges.length === 0) return 0;

  let covered = 0;
  let curStart = ranges[0].start;
  let curEnd = ranges[0].end;

  for (let i = 1; i < ranges.length; i++) {
    const r = ranges[i];
    if (r.start <= curEnd) {
      curEnd = Math.max(curEnd, r.end);
    } else {
      covered += curEnd - curStart;
      curStart = r.start;
      curEnd = r.end;
    }
  }
  covered += curEnd - curStart;
  return covered;
}

export function matchCoverage(spans: ReadonlyArray<MatchSpan>, queryLength: number): number {
  if (queryLength === 0) return 0;
  return coveredLength(spans, queryLength) / queryLength;
}

/**
 * Build a working copy of `query` with every matched span replaced by spaces.
 * This is the single residual path — extractors do not re-declare strip regexes.
 */
export function maskMatchedSpans(query: string, spans: ReadonlyArray<MatchSpan>): string {
  if (spans.length === 0) return query;

  const chars = query.split('');
  for (const span of spans) {
    const start = Math.max(0, Math.min(span.start, chars.length));
    const end = Math.max(0, Math.min(span.end, chars.length));
    for (let i = start; i < end; i++) {
      chars[i] = ' ';
    }
  }
  return chars.join('');
}

/** Extend a count-unit match leftward to include a nearby "exactly" qualifier. */
export function extendSpanForExactly(
  query: string,
  start: number,
  end: number
): { start: number; end: number } {
  const before = query.slice(Math.max(0, start - 16), start);
  const exactlyMatch = before.match(/\bexactly\s+$/i);
  if (exactlyMatch) {
    return { start: start - exactlyMatch[0].length, end };
  }
  return { start, end };
}

export function spanFromRegExpMatch(
  query: string,
  match: RegExpMatchArray,
  field: string,
  value: unknown,
  extras?: { confidence?: number; ruleId?: string; includeExactly?: boolean }
): MatchSpan {
  const index = match.index ?? 0;
  let start = index;
  let end = index + match[0].length;

  if (extras?.includeExactly) {
    const extended = extendSpanForExactly(query, start, end);
    start = extended.start;
    end = extended.end;
  }

  return {
    start,
    end,
    field,
    raw: query.slice(start, end),
    value,
    ...(extras?.confidence !== undefined ? { confidence: extras.confidence } : {}),
    ...(extras?.ruleId ? { ruleId: extras.ruleId } : {}),
  };
}
